import { Row, Select, Tooltip } from 'antd';
import { Controller } from 'react-hook-form';
import arrow_down from 'assets/icons/arrow-down.svg';
import { useState } from 'react';
import { useClickOutside } from 'hooks';
import { useRef } from 'react';
import { HelpInfo, TagsRender } from '.';
import InfoCircleIcon from '../Icons/InfoCircleIcon';
import { useTranslation } from 'react-i18next';
import { useHover } from 'ahooks';
import {
  CorporateList,
  CorporateUserList,
} from 'app/modules/DigitalAssetManagementModule/slice/types';

interface Props {
  control: any;
  inputName: string;
  placeholder?: string;
  defaultValue?: string | number | string[];
  error: any;
  callback?: () => void;
  className?: string;
  arrowIcon?: string;
  options?: {
    key: string | number;
    label: string | JSX.Element;
    disabled?: boolean;
    img?: JSX.Element;
  }[];
  data?: CorporateList | CorporateUserList | null;
  initialData: CorporateList | CorporateUserList | null;
  setData: (e: any) => void;
  disabled?: boolean;
  tags: string[];
  helpInfo?: string[];
  setTags: (e: any) => void;
  colKeys: { key: string; value: string };
  renderList: () => JSX.Element[];
  hidden: boolean;
}

export default function MultiSelect({
  control,
  inputName,
  defaultValue,
  error,
  options,
  arrowIcon = arrow_down,
  disabled,
  tags,
  setTags,
  data,
  setData,
  initialData,
  placeholder,
  helpInfo = [],
  colKeys,
  renderList,
  hidden = false,
  ...props
}: Props) {
  const { t } = useTranslation();
  const hoverRef = useRef(null);
  const isHover = useHover(hoverRef);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const handleClose = (removedTag: any) => {
    setTags(
      tags.filter(tag => tag[colKeys.key] !== removedTag?.value[colKeys.key]),
    );

    setData(initialData);
  };

  const callback = () => {
    if (!visible) return;
    setTimeout(() => {
      setVisible(false);
      setData(initialData);
    }, 300);
  };
  const searchList = (e: string) => {
    const filteredData = initialData?.filter(item =>
      ' '.concat
        .apply([], Object.values(item))
        .toLowerCase()
        .includes(e.toLowerCase()),
    );
    setData(filteredData);
  };
  const elRef = useRef<any>();
  useClickOutside({ elRef, callback });
  return (
    <Controller
      control={control}
      name={inputName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <>
          <div ref={elRef} hidden={hidden}>
            <div className={`custom-input-select`}>
              <Select
                className="vertical-scroll"
                disabled={disabled}
                mode="multiple"
                style={{ width: '100%' }}
                placeholder={placeholder}
                defaultValue={tags}
                value={tags}
                optionLabelProp="label"
                dropdownClassName={'custom-multi-select'}
                onFocus={() => setVisible(true)}
                searchValue={searchText}
                onSearch={e => {
                  setSearchText(e.trimStart());
                  searchList(e.trimStart());
                }}
                tagRender={(e: any) => {
                  return (
                    <TagsRender
                      label={e?.label[colKeys.value]}
                      onClose={() => handleClose(e)}
                      closable={!disabled}
                      value={e?.label[colKeys.value]}
                    />
                  );
                }}
              />
              <div className={'custom-select-tooltip'}>
                <Tooltip
                  title={
                    error?.message ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t(error?.message),
                        }}
                      />
                    ) : (
                      <HelpInfo helpInfo={helpInfo} />
                    )
                  }
                  overlayClassName={
                    error?.message ? 'error-tooltip' : 'help-tooltip'
                  }
                  placement="topRight"
                  color={''}
                  arrowPointAtCenter
                  trigger={
                    window.matchMedia('(any-pointer: coarse)').matches
                      ? 'click'
                      : 'hover'
                  }
                >
                  <div style={{ display: 'flex' }} ref={hoverRef}>
                    {error?.message ? (
                      <InfoCircleIcon />
                    ) : helpInfo.length > 0 ? (
                      <InfoCircleIcon type="help" isHover={isHover} />
                    ) : null}
                  </div>
                </Tooltip>
              </div>
            </div>
            <Row
              id="scrollableDiv"
              className={'custom-multi-checkbox'}
              hidden={!visible}
            >
              <div
                className={'custom-checkbox custom-checkbox-group'}
                style={{ width: '100%', display: 'flex' }}
              >
                {data && data?.length > 0 ? (
                  renderList()
                ) : (
                  <div className="ant-empty ant-empty-normal ant-empty-small">
                    <div className="ant-empty-image">
                      <svg
                        className="ant-empty-img-simple"
                        width="64"
                        height="41"
                        viewBox="0 0 64 41"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g
                          transform="translate(0 1)"
                          fill="none"
                          fillRule="evenodd"
                        >
                          <ellipse
                            className="ant-empty-img-simple-ellipse"
                            cx="32"
                            cy="33"
                            rx="32"
                            ry="7"
                          ></ellipse>
                          <g
                            className="ant-empty-img-simple-g"
                            fillRule="nonzero"
                          >
                            <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                            <path
                              d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
                              className="ant-empty-img-simple-path"
                            ></path>
                          </g>
                        </g>
                      </svg>
                    </div>
                    <div className="ant-empty-description">No Data</div>
                  </div>
                )}
              </div>
            </Row>
          </div>
        </>
      )}
    />
  );
}
